// Configuration constants
const RESULTS_PER_PAGE = 48;
const CURRENT_PAGE = 1;
const MIN_QUERY_LENGTH = 2;
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 10000;
const INVENTORY_THRESHOLD = 10;
const PAGINATION_WINDOW = 2;

// CSS Classes
const CSS_CLASSES = {
  btnAtc: "btn-atc btn btn-md w-100 btn-primary",
  containerAtc: "btn-atc-container",
  containerMobile: "mobile-view d-block d-lg-none",
};

/**
 * Constructor.io Search Integration
 * Handles product search with retry logic, pagination, and fallback support
 */
class ConstructorSearch {
  constructor() {
    this.client = null;
    this.currentRequest = null;
    this.currentQuery = "";
    this.currentPage = 1;
    this.currentFilters = {};
    this.retryCount = 0;
    this._formHandlers = new WeakMap();
    this._domCache = new Map();

    this.initializeClient();
    this.bindEvents();
  }

  initializeClient() {
    if (window.constructorio) {
      this.client = window.constructorio;
    } else if (window.cnstrc?.indexKey && typeof ConstructorioClient !== "undefined") {
      this.client = new ConstructorioClient({
        apiKey: window.cnstrc.indexKey,
        userId: window.cnstrc.userId,
        testCells: window.cnstrc.testCell ? { abTest: window.cnstrc.testCell } : {},
      });
      window.constructorio = this.client;
    }
  }

  getInventoryDisplayText(variant, showInventory) {
    if (!showInventory) {
      return "";
    }

    if (!variant.available) {
      const soldOutText = ConstructorUtils.getThemeConfig("productCard.translations.soldOut") || "Sold out";
      return `<small class="text-muted d-block">${soldOutText}</small>`;
    }

    const inventoryQty = variant.inventory_quantity || 0;

    if (inventoryQty <= 0 || inventoryQty >= INVENTORY_THRESHOLD) {
      const inStockText = ConstructorUtils.getThemeConfig("productCard.translations.inStock") || "In stock";
      return `<small class="text-success d-block">${inStockText}</small>`;
    }

    const stockCountTemplate =
      ConstructorUtils.getThemeConfig("productCard.translations.stockCount") || "Only [count] left in stock";
    const stockText = stockCountTemplate.replace("[count]", inventoryQty);
    return `<small class="text-danger d-block">${stockText}</small>`;
  }

  /**
   * Performs product search using Constructor.io API
   * @param {string} query - Search term
   * @param {Object} options - Search options
   * @param {number} [options.page] - Page number for pagination
   * @param {number} [options.resultsPerPage] - Number of results per page
   * @param {Object} [options.filters] - Filters to apply to search
   * @returns {Promise<boolean>} True if search succeeds, false otherwise
   */
  async handleSearch(query, options = {}) {
    if (!this.client) {
      this.initializeClient();
    }

    if (!this.client) {
      return false;
    }

    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    // Simple: new search = page 1, same search = keep page
    const isNewQuery = this.currentQuery !== query;

    this.currentQuery = query;
    this.currentFilters = options.filters || {};

    // Modern approach: options override everything, no complex merging
    const searchParams = {
      page: isNewQuery ? 1 : options.page || 1,
      resultsPerPage: options.resultsPerPage || RESULTS_PER_PAGE,
      section: "Products",
      ...options,
    };

    if (this.currentFilters && Object.keys(this.currentFilters).length > 0) {
      searchParams.filters = this.currentFilters;
    }

    this.currentPage = searchParams.page;

    const controller = new AbortController();
    this.currentRequest = controller;
    searchParams.signal = controller.signal;

    try {
      this.showLoadingState();

      const response = await this.client.search.getSearchResults(query, searchParams);

      if (controller.signal.aborted) {
        throw new Error("Search request was aborted");
      }

      this.currentRequest = null;

      const results = response.response?.results || response.results || [];
      const totalResults = response.response?.total_num_results || response.total_num_results || results.length;

      const { constructorProducts, productHandles } = results.reduce(
        (acc, result) => {
          const transformedResult = this._transformSearchResult(result);
          if (transformedResult) {
            acc.constructorProducts.push(transformedResult);
            acc.productHandles.push(transformedResult.handle);
          }
          return acc;
        },
        { constructorProducts: [], productHandles: [] }
      );

      await Promise.all([
        this.renderProducts(productHandles, {
          searchTerms: query,
          resultId: response.result_id,
          totalResults,
          constructorProducts,
          currentPage: searchParams.page,
          resultsPerPage: searchParams.resultsPerPage,
          ...options,
        }),
        Promise.resolve(this.updateURL(query, searchParams)),
        Promise.resolve(
          this.renderPagination({
            currentPage: searchParams.page,
            resultsPerPage: searchParams.resultsPerPage,
            totalResults,
            query: this.currentQuery,
          })
        ),
      ]);

      return true;
    } catch (error) {
      return this._handleSearchError(error, query, options);
    }
  }

  /**
   * Handles search errors with retry logic
   * @private
   * @param {Error} error - The error that occurred
   * @param {string} query - Original search query
   * @param {Object} options - Original search options
   * @returns {Promise<boolean>} Result of retry or false
   */
  async _handleSearchError(error, query, options) {
    if (this._shouldRetry(error)) {
      const retryDelay = this._calculateRetryDelay();
      this.retryCount++;

      if (this.retryCount <= MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return this.handleSearch(query, options);
      }
    }

    this.retryCount = 0;
    this.currentRequest = null;
    this.hideLoadingState();
    this.showErrorState("NETWORK_ERROR");
    return false;
  }

  /**
   * Checks if error is retryable (network errors)
   * @private
   * @param {Error} error - Error to check
   * @returns {boolean} True if error is retryable
   */
  _shouldRetry(error) {
    return error.name === "NetworkError" || error.code === "ECONNABORTED";
  }

  /**
   * Check if variant is available based on inventory policy and quantity
   * @private
   * @param {Object} data - Variant data
   * @returns {boolean} True if variant is available
   */
  _isVariantAvailable(data) {
    if (!data) return false;

    const policy = (data.inventory_policy || data.inventoryPolicy || "").toLowerCase();
    const quantity = data.inventory_quantity || 0;

    return policy === "continue" || quantity > 0;
  }

  /**
   * Check URL parameters and perform search if needed (for page loads)
   */
  checkUrlAndSearch() {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get("q");

    if (query && query.trim() && window.location.pathname.includes("/search")) {
      const page = parseInt(urlParams.get("page")) || CURRENT_PAGE;
      const resultsPerPage = parseInt(urlParams.get("per_page")) || RESULTS_PER_PAGE;

      this.handleSearch(query.trim(), {
        page: page,
        resultsPerPage: resultsPerPage,
      });
    }
  }

  /**
   * Calculates retry delay with exponential backoff
   * @private
   * @returns {number} Delay in milliseconds
   */
  _calculateRetryDelay() {
    return Math.min(BASE_DELAY_MS * Math.pow(2, this.retryCount), MAX_DELAY_MS);
  }

  /**
   * Transforms Constructor.io search result to internal format
   * @private
   * @param {Object} item - Raw search result from Constructor.io
   * @returns {Object|null} Transformed product data or null if invalid
   */
  _transformSearchResult(item) {
    const handle = this._extractProductHandle(item);
    if (!handle) return null;

    const variations = item.variations || [];
    const transformedVariants = variations.map((variation) => this._transformVariant(variation, item.data || {}));

    // Extract tags from Constructor.io data (can be array or comma-separated string)
    let tags = item.data?.tags || [];
    if (typeof tags === "string") {
      tags = tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    }

    return {
      handle,
      url: item.data?.url,
      value: item.value,
      constructorData: { data: item.data },
      variations: variations,
      variants: transformedVariants,
      matchedTerms: item.matched_terms || [],
      isSlotted: item.is_slotted || false,
      tags: tags,
    };
  }

  /**
   * Transform Constructor.io variation to Shopify variant format
   * @private
   * @param {Object} variant - Constructor.io variation object
   * @param {Object} parentData - Parent product data
   * @returns {Object} Transformed variant object
   */
  _transformVariant(variant, parentData) {
    const variantTitle = variant.data?.title || variant.value || "Variant";

    let color = variant.data?.color || variant.data?.stone_color || variant.data?.metal;
    let size = variant.data?.size;
    let option1, option2;

    if (!color && !size && variantTitle) {
      if (variantTitle.includes(" / ")) {
        const parts = variantTitle.split(" / ").map((part) => part.trim());
        option1 = parts[0] || undefined;
        option2 = parts[1] || undefined;
      } else {
        option1 = variantTitle.trim();
      }
    }

    return {
      id: variant.data?.variation_id || variant.id,
      title: variantTitle,
      price: variant.data?.price || parentData.price || 0,
      compare_at_price: variant.data?.compareAtPrice || parentData.compareAtPrice || 0,
      inventory_quantity: variant.data?.inventory_quantity || 0,
      available: this._isVariantAvailable(variant.data),
      image_url: variant.data?.image_url,
      color: color,
      size: size,
      option1: option1,
      option2: option2,
      discountPercentage: variant.data?.discountPercentage || variant.data?.fake_discount_percentage || 0,
      featured_image: {
        src: variant.data?.image_url || parentData.image_url || "",
      },
      inventory_policy: variant.data?.inventory_policy || variant.data?.inventoryPolicy || "deny",
    };
  }

  /**
   * Extracts product handle from Constructor.io item
   * @private
   * @param {Object} item - Constructor.io item
   * @returns {string|null} Product handle or null if not found
   */
  _extractProductHandle(item) {
    return (
      item.data?.handle ||
      item.data?.slug ||
      item.data?.shopify_handle ||
      item.data?.url?.split("/products/")[1]?.split("?")[0] ||
      item.value ||
      item.handle ||
      (typeof item.data?.id === "string" && /[a-zA-Z-]/.test(item.data.id) ? item.data.id : null) ||
      (typeof item.data?.item_id === "string" && /[a-zA-Z-]/.test(item.data.item_id) ? item.data.item_id : null)
    );
  }

  /**
   * Renders search results to the page
   * @param {string[]} handles - Array of product handles
   * @param {Object} options - Rendering options
   * @param {string} options.searchTerms - Original search query
   * @param {string} options.resultId - Constructor.io result ID for tracking
   * @param {number} options.totalResults - Total number of results
   * @param {Object[]} options.constructorProducts - Product data from Constructor.io
   * @returns {Promise<void>}
   */
  async renderProducts(handles, options = {}) {
    if (!handles || handles.length === 0) {
      this.showNoResults(options.searchTerms);
      return;
    }

    try {
      const constructorProducts = options.constructorProducts || [];

      if (constructorProducts.length === 0) {
        this.showNoResults(options.searchTerms);
        return;
      }

      const resultsContainer = this.getResultsContainer();

      if (!resultsContainer) {
        this.showErrorState();
        return;
      }

      this.hideDefaultShopifyResults();
      this.updateResultsMessage(options.searchTerms, options.totalResults);

      await this.renderProductsWithOptimizedDOM(constructorProducts, resultsContainer, options);

      const finalGrid = resultsContainer.querySelector("[data-cnstrc-search]");
      if (finalGrid && options.resultId) {
        finalGrid.setAttribute("data-cnstrc-result-id", options.resultId);
        finalGrid.setAttribute("data-cnstrc-num-results", constructorProducts.length);
      }

      this.hideLoadingState();
    } catch (error) {
      this.hideLoadingState();
      this.showErrorState();
    }
  }

  async renderProductsWithOptimizedDOM(products, resultsContainer, options = {}) {
    const grid = resultsContainer.querySelector("[data-cnstrc-search]");
    if (!grid) return;

    const fragment = document.createDocumentFragment();
    const tempDiv = document.createElement("div");

    const CHUNK_SIZE = 12;
    for (let i = 0; i < products.length; i += CHUNK_SIZE) {
      const chunk = products.slice(i, i + CHUNK_SIZE);
      const chunkHTML = chunk
        .map((product, index) => this.renderSingleProductTemplate(product, { ...options, productIndex: i + index }))
        .join("");

      tempDiv.innerHTML = chunkHTML;
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }
    }

    grid.innerHTML = "";
    grid.appendChild(fragment);
    grid.classList.add("constructor-loaded");
    grid.setAttribute("data-cnstrc-num-results", products.length);

    requestAnimationFrame(() => {
      this.reinitializeComponents(grid);
    });
  }

  getSmartAddToCartFormMobile(product) {
    const variants = product.variants || [];

    const settings = window.theme?.productCard;
    if (!settings?.showAtcForm) {
      return "";
    }

    const availableVariants = variants.filter((v) => v.available !== false);

    if (availableVariants.length === 0) {
      return this.generateSoldOutButton("mobile");
    } else if (availableVariants.length === 1) {
      return this.generateSimpleAddToCartButton(product, availableVariants[0], "mobile");
    } else {
      return this.generateDropdownAddToCartButton(product, availableVariants, "mobile");
    }
  }

  renderSingleProductTemplate(product, options = {}) {
    if (!product) return "";

    const config = options.config || ConstructorUtils.getCachedThemeConfig();

    return ConstructorUtils.renderProductCard(product, {
      ...options,
      config,
      renderAddToCartDesktop: (prod) => this.getSmartAddToCartForm(prod),
      renderAddToCartMobile: (prod) => this.getSmartAddToCartFormMobile(prod),
    });
  }

  getResultsContainer() {
    if (!this._domCache.has("resultsContainer")) {
      this._domCache.set("resultsContainer", document.querySelector("#constructor-search-results"));
    }
    return this._domCache.get("resultsContainer");
  }

  updateResultsMessage(searchTerms, totalResults) {
    if (!this._domCache.has("messageElement")) {
      this._domCache.set("messageElement", document.querySelector(".search-results-message"));
    }
    const messageElement = this._domCache.get("messageElement");

    if (messageElement && searchTerms) {
      const message =
        window.searchPageTranslations?.resultsWithCountAndTerm || `Found ${totalResults} results for "${searchTerms}"`;
      messageElement.textContent = message.replace("COUNT_PLACEHOLDER", totalResults).replace("TERMS_PLACEHOLDER", searchTerms);

      messageElement.style.visibility = "visible";
      messageElement.style.height = "";
      messageElement.style.margin = "";
      messageElement.style.padding = "";
    }
  }

  /**
   * Shows loading state (hook for themes to implement)
   * @protected
   */
  showLoadingState() {
    // Hook for themes to show loading spinner/state
  }

  /**
   * Hides loading state (hook for themes to implement)
   * @protected
   */
  hideLoadingState() {
    // Hook for themes to hide loading spinner/state
  }

  showErrorState(errorCode = "GENERIC_ERROR") {
    const container = this.getResultsContainer();
    const grid = container?.querySelector("[data-cnstrc-search]");
    if (grid) {
      const errorMessages = {
        CLIENT_UNAVAILABLE: "Search service is currently unavailable. Please refresh the page.",
        NETWORK_ERROR: "Network error occurred. Please check your connection and try again.",
        GENERIC_ERROR: "Search temporarily unavailable. Please try again.",
      };

      const message = errorMessages[errorCode] || errorMessages["GENERIC_ERROR"];
      grid.innerHTML = `<li class="col-12"><div class="alert alert-warning py-3" role="status">${message}</div></li>`;
    }
  }

  hideDefaultShopifyResults() {
    document.body.classList.add("constructor-search-active");
  }

  showNoResults(searchTerms) {
    const container = this.getResultsContainer();
    if (container) {
      const noResultsEl = document.createElement("p");
      noResultsEl.className = "alert alert-warning py-3";
      noResultsEl.setAttribute("role", "status");
      noResultsEl.setAttribute("data-cnstrc-zero-result", "");
      noResultsEl.textContent = `No results found for "${searchTerms}"`;

      const existingGrid = container.querySelector("[data-cnstrc-search]");
      if (existingGrid) {
        existingGrid.classList.add("constructor-loaded");
        existingGrid.replaceWith(noResultsEl);
      }
    }

    this.hideLoadingState();
  }

  reinitializeComponents(container) {
    document.dispatchEvent(
      new CustomEvent("constructor:products-loaded", {
        detail: { container },
      })
    );

    if (window.ProductCard) {
      container.querySelectorAll(".product-item").forEach((card) => {
        new ProductCard(card);
      });
    }
  }

  updateURL(query, searchParams = {}) {
    const url = new URL(window.location);
    url.searchParams.set("q", query);
    url.searchParams.set("type", "product");

    // Only add page if > 1
    if (searchParams.page && searchParams.page > 1) {
      url.searchParams.set("page", searchParams.page);
    } else {
      url.searchParams.delete("page");
    }

    // Only add per_page if different from default
    if (searchParams.resultsPerPage && searchParams.resultsPerPage !== RESULTS_PER_PAGE) {
      url.searchParams.set("per_page", searchParams.resultsPerPage);
    } else {
      url.searchParams.delete("per_page");
    }

    window.history.pushState(null, "", url.toString());
  }

  renderPagination(options) {
    const { currentPage, resultsPerPage, totalResults } = options;
    const totalPages = Math.ceil(totalResults / resultsPerPage);

    if (totalPages <= 1) {
      this.hidePagination();
      return;
    }

    let paginationContainer = document.querySelector(".constructor-pagination");
    if (!paginationContainer) {
      const resultsContainer = this.getResultsContainer();
      if (resultsContainer) {
        paginationContainer = document.createElement("div");
        paginationContainer.className = "constructor-pagination d-flex justify-content-center mt-4";
        resultsContainer.appendChild(paginationContainer);
      }
    }

    if (!paginationContainer) return;

    const paginationHTML = this.generatePaginationHTML(currentPage, totalPages, this.currentQuery, resultsPerPage);
    paginationContainer.innerHTML = paginationHTML;

    this.bindPaginationEvents(paginationContainer);

    paginationContainer.style.display = "flex";
  }

  generatePaginationHTML(currentPage, totalPages, query, resultsPerPage) {
    const createPageLink = (page, label = page, disabled = false, active = false) => {
      if (disabled) return `<li class="page-item disabled"><span class="page-link">${label}</span></li>`;
      if (active) return `<li class="page-item active"><span class="page-link">${label}</span></li>`;
      return `<li class="page-item"><a class="page-link constructor-page-link" href="#" data-page="${page}" data-per-page="${resultsPerPage}">${label}</a></li>`;
    };

    const pages = [];

    pages.push(
      createPageLink(
        currentPage - 1,
        '<span aria-hidden="true">&laquo;</span><span class="sr-only">Previous</span>',
        currentPage <= 1
      )
    );

    const startPage = Math.max(1, currentPage - PAGINATION_WINDOW);
    const endPage = Math.min(totalPages, currentPage + PAGINATION_WINDOW);

    if (startPage > 1) {
      pages.push(createPageLink(1));
      if (startPage > 2) pages.push(createPageLink(null, "...", true));
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(createPageLink(i, i, false, i === currentPage));
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push(createPageLink(null, "...", true));
      pages.push(createPageLink(totalPages));
    }

    pages.push(
      createPageLink(
        currentPage + 1,
        '<span aria-hidden="true">&raquo;</span><span class="sr-only">Next</span>',
        currentPage >= totalPages
      )
    );

    return `<nav aria-label="Search results pagination"><ul class="pagination mb-0">${pages.join("")}</ul></nav>`;
  }

  bindPaginationEvents(container) {
    container.querySelectorAll(".constructor-page-link").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const page = parseInt(link.dataset.page);
        const perPage = parseInt(link.dataset.perPage);

        this.getResultsContainer()?.scrollIntoView({ behavior: "smooth", block: "start" });
        // Use current query, not stale query from link
        this.handleSearch(this.currentQuery, { page, resultsPerPage: perPage });
      });
    });
  }

  hidePagination() {
    document.querySelector(".constructor-pagination")?.style.setProperty("display", "none");
  }

  bindEvents() {
    const forms = [];

    const mainForm = document.getElementById("main-search-form");
    if (mainForm) {
      forms.push({ form: mainForm, type: "main-search" });
    }

    const offcanvasForm = document.querySelector("[data-cnstrc-search-form]");
    if (offcanvasForm && offcanvasForm !== mainForm) {
      forms.push({ form: offcanvasForm, type: "offcanvas-search" });
    }

    const genericForms = document.querySelectorAll('form[action*="/search"]');
    genericForms.forEach((form) => {
      if (!forms.some((f) => f.form === form)) {
        forms.push({ form: form, type: "generic-search" });
      }
    });

    if (forms.length > 0) {
      forms.forEach(({ form }) => {
        const oldHandler = this._formHandlers.get(form);
        if (oldHandler) {
          form.removeEventListener("submit", oldHandler);
        }

        const handler = this.handleFormSubmit.bind(this);
        this._formHandlers.set(form, handler);
        form.addEventListener("submit", handler);
      });
    }

    document.addEventListener("click", (e) => {
      const swatch = e.target.closest('[data-action="change-swatch"]');
      if (!swatch) return;

      e.preventDefault();

      const productItem = swatch.closest(".product-item");
      if (!productItem) return;

      productItem.querySelectorAll(".color-swatches .btn").forEach((s) => s.classList.remove("selected"));
      swatch.classList.add("selected");

      const img = productItem.querySelector(".product-item-img--primary");
      const url = swatch.dataset.img?.trim();
      if (img && url) {
        img.src = url;
        img.srcset = url;
        img.removeAttribute("sizes");
        if (swatch.dataset.imgWidth) img.width = swatch.dataset.imgWidth;
        if (swatch.dataset.imgHeight) img.height = swatch.dataset.imgHeight;
      }
    });

    window.addEventListener("popstate", () => {
      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get("q");
      const page = parseInt(urlParams.get("page")) || 1;
      const perPage = parseInt(urlParams.get("per_page")) || RESULTS_PER_PAGE;

      if (query && (query !== this.currentQuery || page !== this.currentPage)) {
        this.handleSearch(query, { page, resultsPerPage: perPage });
      }
    });
  }

  /**
   * Modern form submission with enhanced validation and error handling
   */
  async handleFormSubmit(e) {
    try {
      const formData = new FormData(e.target);
      const searchTerm = formData.get("q")?.trim();
      const searchType = formData.get("type");

      if (!searchTerm) {
        return;
      }

      if (searchTerm.length < MIN_QUERY_LENGTH) {
        return;
      }

      const isOffcanvas = e.target.closest("#offcanvas-search") !== null;
      const isSearchPage = window.location.pathname.includes("/search");

      if (isOffcanvas && searchTerm && !isSearchPage) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const isProductSearch = !searchType || searchType.includes("product");

      if (isProductSearch) {
        const success = await this.handleSearch(searchTerm, { page: 1 });

        if (!success) {
          this.fallbackToNativeSearch(e.target);
        }
      } else {
        this.fallbackToNativeSearch(e.target);
      }
    } catch (error) {
      this.fallbackToNativeSearch(e.target);
    }
  }

  fallbackToNativeSearch(form) {
    const handler = this._formHandlers.get(form);
    if (handler) {
      form.removeEventListener("submit", handler);
      this._formHandlers.delete(form);
    }

    form.submit();
  }

  getSmartAddToCartForm(product, deviceType = "desktop") {
    const settings = ConstructorUtils.getThemeConfig("productCard");

    if (!settings?.showAtcForm) {
      return "";
    }

    const isCustomizedProduct =
      product.collections?.some((collection) => collection.handle === "customized") ||
      product.tags?.some((tag) => tag === "customized");

    if (isCustomizedProduct) {
      return this.generateViewProductButton(product, deviceType);
    }

    const validation = ConstructorUtils.validateProduct(product);
    if (!validation.isValid) {
      return "";
    }

    const availableVariants = product.variants.filter((variant) => variant.available);

    if (availableVariants.length === 0) {
      return this.generateSoldOutButton(deviceType);
    }

    if (product.has_only_default_variant || availableVariants.length === 1) {
      return this.generateSimpleAddToCartButton(product, availableVariants[0], deviceType);
    }

    return this.generateDropdownAddToCartButton(product, availableVariants, deviceType);
  }

  generateViewProductButton(product, deviceType = "desktop") {
    const productUrl = product.url || `/products/${product.handle}`;
    const isMobile = deviceType === "mobile";

    const containerClass = isMobile ? CSS_CLASSES.containerMobile : CSS_CLASSES.containerAtc;
    const buttonClass = isMobile ? "btn-vp w-100 d-block" : "view-product-btn-desktop";
    const buttonId = isMobile ? "new-view-product-btn-mobile" : "btn-vp-desktop";
    const wrapperDiv = isMobile ? "" : '<div class="mb-2">';
    const wrapperDivEnd = isMobile ? "" : "</div>";

    return `
      <div class="${containerClass}">
        ${wrapperDiv}
          <a href="${productUrl}" 
             class="${buttonClass}"
             id="${buttonId}"
             role="button">
            ${ConstructorUtils.getThemeConfig("productCard.translations.viewProduct", "View Product")}
          </a>
        ${wrapperDivEnd}
      </div>
    `;
  }

  generateSoldOutButton(deviceType = "desktop") {
    const isMobile = deviceType === "mobile";

    const containerClass = isMobile ? CSS_CLASSES.containerMobile : CSS_CLASSES.containerAtc;
    const wrapperDiv = isMobile ? "" : '<div class="mb-2">';
    const wrapperDivEnd = isMobile ? "" : "</div>";
    const buttonId = isMobile ? "" : 'id="btn-atc-desktop"';

    return `
      <div class="${containerClass}">
        ${wrapperDiv}
          <button class="${CSS_CLASSES.btnAtc}" ${buttonId} disabled>
            ${ConstructorUtils.getThemeConfig("productCard.translations.soldOut", "Sold Out")}
          </button>
        ${wrapperDivEnd}
      </div>
    `;
  }

  generateSimpleAddToCartButton(product, variant, deviceType = "desktop") {
    const isMobile = deviceType === "mobile";

    const productUrl = product.url || `/products/${product.handle}`;

    const containerClass = isMobile ? CSS_CLASSES.containerMobile : CSS_CLASSES.containerAtc;
    const wrapperDiv = isMobile ? "" : '<div class="mb-2">';
    const wrapperDivEnd = isMobile ? "" : "</div>";
    const buttonId = isMobile ? "" : 'id="btn-atc-desktop"';
    const addToCartText = isMobile
      ? ConstructorUtils.getThemeConfig("productCard.translations.addToCart", "Add to cart")
      : ConstructorUtils.getThemeConfig("productCard.translations.addToCart", "Add To Cart");

    return `
      <div class="${containerClass}">
        ${wrapperDiv}
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${product.title}"
                data-product-url="${productUrl}">
            <input type="hidden" name="id" value="${variant.id}">
            <button class="${CSS_CLASSES.btnAtc}"
                    type="submit"
                    ${buttonId}
                    name="add"
                    data-text-add-to-cart="${addToCartText}">
              ${addToCartText}
            </button>
          </form>
        ${wrapperDivEnd}
      </div>
    `;
  }

  generateDropdownAddToCartButton(product, availableVariants, deviceType = "desktop") {
    const translations = window.theme?.productCard?.translations;
    const showInventory = window.theme?.productCard?.formShowInventory;
    const isMobile = deviceType === "mobile";

    const productUrl = product.url || `/products/${product.handle}`;

    const dropdownItems = availableVariants
      .map((variant) => this.generateVariantDropdownItem(variant, showInventory, translations, deviceType))
      .join("");

    const containerClass = isMobile ? "mobile-view d-block d-lg-none" : "btn-atc-container";
    const wrapperDiv = isMobile ? "" : '<div class="mb-2">';
    const wrapperDivEnd = isMobile ? "" : "</div>";
    const dropdownClass = isMobile ? "w-100 dropup" : "w-200 dropup";
    const buttonClass = isMobile ? "btn-atc w-100" : "btn-atc btn btn-md dropdown-toggle btn-primary w-200";
    const buttonId = isMobile ? "new-add-to-bag-btn-mobile" : "btn-atc-desktop";
    const menuClass = isMobile ? "dropdown-menu shadow w-100" : "dropdown-menu shadow w-200";
    const addToCartText = isMobile ? translations?.addToCart || "Add to cart" : translations?.addToCart || "Add To Cart";

    return `
      <div class="${containerClass}">
        ${wrapperDiv}
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${product.title}"
                data-product-url="${productUrl}">
            <input type="hidden" name="id" value="${availableVariants[0].id}">
            <div class="${dropdownClass}">
              <button type="button"
                      class="${buttonClass}"
                      id="${buttonId}"
                      data-bs-toggle="dropdown"
                      data-text-add-to-cart="${addToCartText}"
                      aria-expanded="false">
                ${addToCartText}
              </button>
              <ul class="${menuClass}">
                ${dropdownItems}
              </ul>
            </div>
          </form>
        ${wrapperDivEnd}
      </div>
    `;
  }

  generateVariantDropdownItem(variant, showInventory, translations, deviceType = "desktop") {
    const inventoryDisplay = this.getInventoryDisplayText(variant, showInventory, translations);
    const isMobile = deviceType === "mobile";
    const flexClass = isMobile
      ? "d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center lh-sm"
      : "d-flex flex-column justify-content-start lh-sm";

    return `
      <li class="${variant.inventory_policy || ""}">
        <button class="dropdown-item"
                onclick="onClickAtcFormVariant(this, event)"
                data-variant-id="${variant.id}"
                type="button"
                data-cnstrc-btn="add_to_cart"
                ${!variant.available ? "disabled" : ""}>
          <div class="${flexClass}">
            <span class="small">${variant.title}</span>
            ${inventoryDisplay}
          </div>
        </button>
      </li>
    `;
  }
}

window.showAllConstructorSwatchesInProduct = (btn, event) => {
  event.preventDefault();
  event.stopPropagation();

  const swatchesList = btn.closest(".color-swatches");
  if (!swatchesList) {
    return;
  }

  swatchesList.querySelectorAll("li[hidden]").forEach((li) => {
    li.removeAttribute("hidden");
  });

  btn.remove();
};

function initConstructorSearch() {
  if (!window.ConstructorUtils || typeof ConstructorioClient === "undefined") {
    setTimeout(initConstructorSearch, 50);
    return;
  }

  if (!window.constructorSearch) {
    const shouldInitialize =
      document.getElementById("search") ||
      document.querySelector("[data-cnstrc-search]") ||
      document.querySelector("[data-cnstrc-search-form]");

    if (shouldInitialize) {
      window.constructorSearch = new ConstructorSearch();
      window.constructorSearch.hideDefaultShopifyResults();
      window.constructorSearch.checkUrlAndSearch();
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initConstructorSearch);
} else {
  initConstructorSearch();
}
